import { useState } from "react";
import { useLedgerNeed } from "../../app/use-ledger-need";
import LoadingLabel from "../../components/LoadingLabel";
import type { Need } from "../../lib/coverage";
import { PiggyBank, Plane, Plus, Wallet } from "lucide-react";
import type { DashboardData, Budget, BudgetOwner, Owner } from "../../types";
import { OVERALL_CATEGORY, budgetedPairs, categorySpend, resolveBudget } from "../../lib/budgets";
import { money, todayIn } from "../../lib/finance";
import Avatar from "../../components/Avatar";
import { categoryIcon } from "../../components/CategoryIcon";
import { Private } from "../../components/Privacy";
import { useProfileNames } from "../../components/ProfilesContext";
import SecondaryHeading from "../../app/SecondaryHeading";
import Modal from "../../components/Modal";
import BudgetProgressBar from "./BudgetProgressBar";
import TripBudgetRow from "./TripBudgetRow";
import BudgetForm from "./BudgetForm";

export default function BudgetSettings({
  data,
  ensure,
  profile,
  onOpenTrip,
}: {
  data: DashboardData;
  /** Loads the ledger back far enough to total the month being viewed. */
  ensure: (need: Need) => unknown;
  profile: Owner;
  onOpenTrip?: (id: string) => void;
}) {
  const allOwners: BudgetOwner[] = [...useProfileNames(), "Household"];
  const activeTripsWithBudget = (data.trips || []).filter(
    (t) => (t.status === "active" || t.status === "planned") && t.budget !== null,
  );
  const currentMonth = todayIn(data.timezone).slice(0, 7);
  const [month, setMonth] = useState(() => new URLSearchParams(window.location.search).get("month") || currentMonth);
  const monthReady = useLedgerNeed(data, month, ensure);
  const [editing, setEditing] = useState<{ category: string; owner: BudgetOwner } | null>(null);
  const [adding, setAdding] = useState(false);
  // You can add a budget for yourself or the shared Household figure, but not on the other
  // profile's behalf — existing budgets from either profile still show here for visibility.
  const addableOwners: BudgetOwner[] = [profile, "Household"];

  const [filter, setFilter] = useState<"all" | "over" | "categories" | "trips">("all");

  const budgets = data.budgets || [];
  const pairs = budgetedPairs(budgets);
  const categories = [
    ...new Set(pairs.filter((p) => p.category !== OVERALL_CATEGORY).map((p) => p.category)),
  ].sort();
  // Who has a budget in force for the month being viewed. A budget set for one month only (not
  // repeating) leaves a history row but no budget now, so owners are judged by what resolves.
  const budgetedIn = (category: string) =>
    allOwners.filter((o) => {
      const b = resolveBudget(budgets, o, category, month);
      return !!b && b.amount !== null;
    });
  const overallOwners = budgetedIn(OVERALL_CATEGORY);
  const months = [...new Set([month, ...Array.from({ length: 12 }, (_, i) => {
    const d = new Date(currentMonth + "-01T12:00:00");
    d.setUTCMonth(d.getUTCMonth() - i);
    return d.toISOString().slice(0, 7);
  })])].sort().reverse();
  const sections: { category: string; label: string; owners: BudgetOwner[] }[] = [
    { category: OVERALL_CATEGORY, label: "Overall", owners: overallOwners },
    ...categories.map((category) => ({ category, label: category, owners: budgetedIn(category) })),
  ];

  // Active budget rows for summary analytics
  const activeBudgetRows = sections.flatMap(({ category, owners }) =>
    owners.map((owner) => {
      const budget = resolveBudget(budgets, owner, category, month);
      if (!budget || budget.amount === null) return null;
      const spent = categorySpend(data.transactions || [], owner, category, month);
      const remaining = budget.amount - spent;
      return {
        category,
        owner,
        budget,
        spent,
        remaining,
        over: remaining < 0,
        pct: budget.amount > 0 ? (spent / budget.amount) * 100 : 0,
      };
    }).filter(Boolean) as {
      category: string;
      owner: BudgetOwner;
      budget: Budget;
      spent: number;
      remaining: number;
      over: boolean;
      pct: number;
    }[],
  );

  const overallRows = activeBudgetRows.filter((r) => r.category === OVERALL_CATEGORY);
  const categoryRows = activeBudgetRows.filter((r) => r.category !== OVERALL_CATEGORY);

  const primaryOverall =
    overallRows.find((r) => r.owner === profile) ||
    overallRows.find((r) => r.owner === "Household") ||
    overallRows[0];

  const totalBudgeted =
    (primaryOverall
      ? primaryOverall.budget.amount
      : categoryRows.reduce((sum, r) => sum + (r.budget.amount ?? 0), 0)) ?? 0;

  const totalSpent = primaryOverall
    ? primaryOverall.spent
    : categoryRows.reduce((sum, r) => sum + r.spent, 0);

  const totalCurrency = primaryOverall?.budget.currency || data.baseCurrency || "USD";
  const totalRemaining = totalBudgeted - totalSpent;
  const isOverallOver = totalRemaining < 0;
  const overallPct = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0;

  const isCurrentMonth = month === currentMonth;
  const [year, monthNum] = month.split("-").map(Number);
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  const todayDay = Number(todayIn(data.timezone).slice(8, 10));
  const daysLeft = isCurrentMonth ? Math.max(0, daysInMonth - todayDay) : 0;
  const dailyPace =
    isCurrentMonth && daysLeft > 0 && totalRemaining > 0
      ? Math.round(totalRemaining / daysLeft)
      : null;

  const overBudgetCount = activeBudgetRows.filter((r) => r.over).length;
  const nearCapCount = activeBudgetRows.filter((r) => !r.over && r.pct >= 80).length;
  const onTrackCount = activeBudgetRows.filter((r) => !r.over && r.pct < 80).length;

  const displayedSections = sections.filter(({ category, owners }) => {
    if (filter === "trips") return false;
    if (filter === "categories") return category !== OVERALL_CATEGORY;
    if (filter === "over") {
      return owners.some((owner) => {
        const budget = resolveBudget(budgets, owner, category, month);
        if (!budget || budget.amount === null) return false;
        const spent = categorySpend(data.transactions || [], owner, category, month);
        return budget.amount - spent < 0;
      });
    }
    return true;
  });

  const showTripBudgets = (filter === "all" || filter === "trips") && activeTripsWithBudget.length > 0;

  function reload() {
    window.location.reload();
  }

  function Row({ category, owner }: { category: string; owner: BudgetOwner }) {
    const budget = resolveBudget(budgets, owner, category, month);
    if (!budget || budget.amount === null) return null;
    // Spending for a month before the recent ones is still on its way.
    if (!monthReady) return <div className="budget-row" role="status"><LoadingLabel>Loading</LoadingLabel></div>;
    const spent = categorySpend(data.transactions || [], owner, category, month);
    const remaining = budget.amount - spent;
    const over = remaining < 0;
    const pct = budget.amount > 0 ? Math.round((spent / budget.amount) * 100) : 0;
    return (
      <button
        type="button"
        className={"budget-row budget-row-button" + (over ? " over" : "")}
        onClick={() => setEditing({ category, owner })}
      >
        <span className="budget-row-main">
          <span className="budget-row-owner">
            <Avatar owner={owner} size={18} />
            <strong>{owner}</strong>
            {!budget.repeats && <span className="budget-row-badge">This month only</span>}
          </span>
          <span className={`budget-status-pill ${over ? "over" : "left"}`}>
            <Private>{money(Math.abs(remaining), budget.currency)}</Private>
            <small>{over ? "over" : "left"}</small>
          </span>
        </span>
        <div className="budget-row-progress-line">
          <BudgetProgressBar spent={spent} amount={budget.amount} />
          <span className="budget-row-figures">
            <Private>{money(spent, budget.currency)}</Private>
            <i>/</i>
            {money(budget.amount, budget.currency)}
            <small className="budget-pct-text">({pct}%)</small>
          </span>
        </div>
      </button>
    );
  }

  return (
    <section className="panel settings-panel budget-settings">
      <SecondaryHeading title="Budgets" icon={<PiggyBank size={15} />}>
        <div className="budget-heading-actions">
          <label className="budget-month-picker">
            <span className="sr-only">Month</span>
            <select
              className="budget-toolbar-control"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Budget month"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {new Date(m + "-01T12:00:00").toLocaleDateString("en-GB", {
                    month: "short",
                    year: "numeric",
                  })}
                </option>
              ))}
            </select>
          </label>
          {!adding && (
            <button
              type="button"
              className="primary small budget-toolbar-control"
              onClick={() => {
                setAdding(true);
                setEditing(null);
              }}
            >
              <Plus size={13} strokeWidth={2.5} /> Add
            </button>
          )}
        </div>
      </SecondaryHeading>

      {/* Monthly Budget Summary Card */}
      {activeBudgetRows.length > 0 && (
        <div className="budget-summary-card">
          <div className="budget-summary-top">
            <div className="budget-summary-headline">
              <span className="budget-summary-eyebrow">
                {primaryOverall ? `${primaryOverall.owner} Overall Budget` : "Monthly Budget Cap"}
              </span>
              <div className="budget-summary-numbers">
                <strong className="budget-summary-spent">
                  <Private>{money(totalSpent, totalCurrency)}</Private>
                </strong>
                <span className="budget-summary-divider">of</span>
                <span className="budget-summary-cap">
                  <Private>{money(totalBudgeted, totalCurrency)}</Private>
                </span>
              </div>
            </div>
            <div className={`budget-summary-status ${isOverallOver ? "over" : "left"}`}>
              <span className="budget-summary-rem-val">
                <Private>{money(Math.abs(totalRemaining), totalCurrency)}</Private>
              </span>
              <small>{isOverallOver ? "over budget" : "left"}</small>
            </div>
          </div>

          <div className="budget-summary-progress">
            <div className={`budget-bar-track ${isOverallOver ? "over" : overallPct >= 80 ? "warn" : ""}`}>
              <div
                className="budget-bar-fill"
                style={{ width: `${Math.min(100, Math.max(0, overallPct))}%` }}
              />
            </div>
          </div>

          <div className="budget-summary-foot">
            <div className="budget-summary-pacing">
              {isCurrentMonth ? (
                daysLeft > 0 ? (
                  <span>
                    <strong>{daysLeft} days left</strong>
                    {dailyPace !== null && (
                      <> · safe to spend <Private>{money(dailyPace, totalCurrency)}/day</Private></>
                    )}
                  </span>
                ) : (
                  <span>Last day of month</span>
                )
              ) : (
                <span>{new Date(month + "-01T12:00:00").toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
              )}
            </div>
            <div className="budget-summary-badges">
              {overBudgetCount > 0 && (
                <span className="budget-health-chip over">
                  {overBudgetCount} over
                </span>
              )}
              {nearCapCount > 0 && (
                <span className="budget-health-chip warn">
                  {nearCapCount} near cap
                </span>
              )}
              {onTrackCount > 0 && (
                <span className="budget-health-chip good">
                  {onTrackCount} on track
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      {(overBudgetCount > 0 || activeTripsWithBudget.length > 0 || categoryRows.length > 0) && activeBudgetRows.length > 0 && (
        <div className="budget-filter-tabs" role="tablist" aria-label="Filter budgets">
          <button
            type="button"
            role="tab"
            aria-selected={filter === "all"}
            className={`budget-tab-btn${filter === "all" ? " active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All <span>({activeBudgetRows.length + activeTripsWithBudget.length})</span>
          </button>
          {overBudgetCount > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "over"}
              className={`budget-tab-btn${filter === "over" ? " active" : ""}`}
              onClick={() => setFilter("over")}
            >
              Over budget <span>({overBudgetCount})</span>
            </button>
          )}
          {categoryRows.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "categories"}
              className={`budget-tab-btn${filter === "categories" ? " active" : ""}`}
              onClick={() => setFilter("categories")}
            >
              Categories <span>({categoryRows.length})</span>
            </button>
          )}
          {activeTripsWithBudget.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={filter === "trips"}
              className={`budget-tab-btn${filter === "trips" ? " active" : ""}`}
              onClick={() => setFilter("trips")}
            >
              Trips <span>({activeTripsWithBudget.length})</span>
            </button>
          )}
        </div>
      )}

      {adding && (
        <Modal title="New budget" onClose={() => setAdding(false)}>
          <BudgetForm
            data={data}
            allowPickTarget
            category={OVERALL_CATEGORY}
            owner={profile}
            addableOwners={addableOwners}
            month={month}
            onDone={() => {
              setAdding(false);
              reload();
            }}
          />
        </Modal>
      )}

      {editing && (
        <Modal
          title={`Edit budget · ${editing.category === OVERALL_CATEGORY ? "Overall spending" : editing.category}`}
          onClose={() => setEditing(null)}
        >
          <BudgetForm
            data={data}
            category={editing.category}
            owner={editing.owner}
            month={month}
            existing={(() => {
              const b = resolveBudget(budgets, editing.owner, editing.category, month);
              return b ? { amount: b.amount, repeats: b.repeats, currency: b.currency } : undefined;
            })()}
            onDone={() => {
              setEditing(null);
              reload();
            }}
          />
        </Modal>
      )}

      <div className="budget-list">
        {showTripBudgets && (
          <section className="budget-group-card trip-budgets-section">
            <div className="budget-group-head">
              <div className="budget-group-title-wrap">
                <span className="budget-group-icon trip"><Plane size={15} /></span>
                <div className="budget-group-text">
                  <h3>Trip Budgets</h3>
                  <small className="budget-group-total">{activeTripsWithBudget.length} active</small>
                </div>
              </div>
            </div>
            <div className="budget-rows-wrap">
              {activeTripsWithBudget.map((trip) => (
                <TripBudgetRow key={trip.id} trip={trip} data={data} onOpenTrip={onOpenTrip} />
              ))}
            </div>
          </section>
        )}
        {displayedSections.map(({ category, label, owners }) => {
          const canAdd = addableOwners.filter((o) => !owners.includes(o));
          const showDraft =
            editing?.category === category && !owners.includes(editing.owner);
          // Historical categories with nothing in force this month stay out of the list.
          // Overall still shows when empty so the compact +Bun/+Household buttons remain.
          if (!owners.length && !showDraft && category !== OVERALL_CATEGORY) return null;
          const GroupIcon = category === OVERALL_CATEGORY ? Wallet : categoryIcon(category);
          const categorySpendVal = categorySpend(data.transactions || [], "Household", category, month);
          return (
            <section className="budget-group-card" key={category}>
              <div className="budget-group-head">
                <div className="budget-group-title-wrap">
                  <span className="budget-group-icon"><GroupIcon size={15} /></span>
                  <div className="budget-group-text">
                    <h3>{category === OVERALL_CATEGORY ? "Overall spending" : label}</h3>
                    {categorySpendVal > 0 && category !== OVERALL_CATEGORY && (
                      <small className="budget-group-total">
                        <Private>{money(categorySpendVal, data.baseCurrency || "USD")}</Private> spent
                      </small>
                    )}
                  </div>
                </div>
                {canAdd.length > 0 && !adding && (
                  <div className="budget-group-add">
                    {canAdd.map((o) => (
                      <button
                        key={o}
                        type="button"
                        className="budget-chip"
                        onClick={() => setEditing({ category, owner: o })}
                        aria-label={`Add budget for ${o}`}
                      >
                        <Avatar owner={o} size={15} />
                        <span>+{o}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="budget-rows-wrap">
                {owners.map((owner) => (
                  <Row key={owner} category={category} owner={owner} />
                ))}
                {showDraft && editing && <Row category={category} owner={editing.owner} />}
              </div>
            </section>
          );
        })}
        {!displayedSections.some((s) => s.owners.length) && !showTripBudgets && !adding && (
          <div className="empty-small">
            <PiggyBank size={28} />
            <p>No budgets found for this view. Add an overall or category cap.</p>
          </div>
        )}
      </div>
    </section>
  );
}
